// src/products/userInvites/components/BulkInviteModal.js
// Purpose: Modal for bulk invite operations including CSV import and multi-product access

import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  Users, 
  X, 
  CheckCircle, 
  AlertCircle, 
  FileText,
  Plus,
  Trash2,
  Database,
  Building,
  Users as UsersIcon,
  HelpCircle,
  Send
} from 'lucide-react';
import { userInvitesApi } from '../api/userInvitesApi';

const BulkInviteModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'csv'
  const [manualInvites, setManualInvites] = useState([{
    email: '',
    products: ['dataroom'],
    role: 'viewer',
    customMessage: ''
  }]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });

  const products = [
    { id: 'dataroom', name: 'Dataroom', icon: Database, description: 'Document sharing' },
    { id: 'capraise', name: 'CapRaise', icon: Building, description: 'Capital raising' },
    { id: 'equity', name: 'Equity', icon: UsersIcon, description: 'Cap table tracking' },
    { id: 'questions', name: 'Questions', icon: HelpCircle, description: 'Q&A discussions' }
  ];

  // Users can only invite Viewers - Creator role requires a paid subscription
  const roles = [
    { id: 'viewer', name: 'Viewer', description: 'Can view content' }
    // Support level is not yet functional - hidden from end users
    // { id: 'support', name: 'Support', description: 'Can view, upload, and organize (cannot create/delete projects or see billing)' },
    // Creator role cannot be assigned via invite - users must subscribe to become Creators
    // { id: 'creator', name: 'Creator', description: 'Full access including user management' }
  ];

  // Manual invite functions
  const addManualInvite = () => {
    setManualInvites([...manualInvites, {
      email: '',
      products: ['dataroom'],
      role: 'viewer',
      customMessage: ''
    }]);
  };

  const removeManualInvite = (index) => {
    if (manualInvites.length > 1) {
      setManualInvites(manualInvites.filter((_, i) => i !== index));
    }
  };

  const updateManualInvite = (index, field, value) => {
    const updated = [...manualInvites];
    updated[index] = { ...updated[index], [field]: value };
    setManualInvites(updated);
  };

  const toggleProduct = (inviteIndex, productId) => {
    const invite = manualInvites[inviteIndex];
    const updatedProducts = invite.products.includes(productId)
      ? invite.products.filter(p => p !== productId)
      : [...invite.products, productId];
    
    updateManualInvite(inviteIndex, 'products', updatedProducts);
  };

  // CSV functions
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setCsvErrors([]);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseCsvData(text);
    };
    reader.readAsText(file);
  };

  const parseCsvData = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    const errors = [];
    const data = [];

    // Validate headers
    const requiredHeaders = ['email', 'products', 'role'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
      setCsvErrors(errors);
      return;
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Validate row
      const rowErrors = [];
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.email)) {
        rowErrors.push(`Invalid email: ${row.email}`);
      }

      // Products validation
      const validProducts = products.map(p => p.id);
      const userProducts = row.products ? row.products.split(';').map(p => p.trim()) : [];
      const invalidProducts = userProducts.filter(p => !validProducts.includes(p));
      
      if (invalidProducts.length > 0) {
        rowErrors.push(`Invalid products: ${invalidProducts.join(', ')}`);
      }

      // Role validation
      const validRoles = roles.map(r => r.id);
      if (!validRoles.includes(row.role)) {
        rowErrors.push(`Invalid role: ${row.role}`);
      }

      if (rowErrors.length > 0) {
        errors.push(`Row ${i + 1}: ${rowErrors.join(', ')}`);
      } else {
        data.push({
          email: row.email,
          products: userProducts.length > 0 ? userProducts : ['dataroom'],
          role: row.role || 'viewer',
          customMessage: row.custommessage || ''
        });
      }
    }

    setCsvData(data);
    setCsvErrors(errors);
  };

  const downloadTemplate = () => {
    const headers = ['email', 'products', 'role', 'custommessage'];
    const sampleData = [
      ['user1@example.com', 'dataroom;capraise', 'viewer', 'Welcome to our platform!'],
      // Support level is not yet functional - hidden from end users
      // ['user2@example.com', 'equity', 'support', ''],
      ['user2@example.com', 'equity', 'viewer', ''],
      ['user3@example.com', 'dataroom;capraise;equity', 'creator', 'Full access granted']
    ];
    
    const csvContent = [headers, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_invite_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Send bulk invites
  const sendBulkInvites = async () => {
    const invites = activeTab === 'manual' ? manualInvites : csvData;
    
    // Validate invites
    const validInvites = invites.filter(invite => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return invite.email && emailRegex.test(invite.email) && invite.products.length > 0;
    });

    if (validInvites.length === 0) {
      setMessage({ type: 'error', text: 'No valid invites to send.' });
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: validInvites.length, status: 'Starting bulk invite...' });
    setMessage(null);

    try {
      const results = {
        success: 0,
        failed: 0,
        errors: []
      };

      for (let i = 0; i < validInvites.length; i++) {
        const invite = validInvites[i];
        setProgress({ 
          current: i + 1, 
          total: validInvites.length, 
          status: `Sending invite to ${invite.email}...` 
        });

        try {
          // Send separate invite for each product
          for (const productId of invite.products) {
            const inviteData = {
              email: invite.email,
              inviteforproduct: productId,
              role: invite.role,
              invitedby: user.id,
              custom_message: invite.customMessage
            };

            const response = await userInvitesApi.createInvite(inviteData);
            
            if (response.status !== 'success') {
              results.errors.push(`${invite.email} (${productId}): ${response.message}`);
              results.failed++;
            } else {
              results.success++;
            }
          }
        } catch (error) {
          results.errors.push(`${invite.email}: ${error.message}`);
          results.failed++;
        }

        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setProgress({ current: validInvites.length, total: validInvites.length, status: 'Complete!' });
      
      if (results.failed === 0) {
        setMessage({
          type: 'success',
          text: `Successfully sent ${results.success} invitations!`
        });
      } else {
        setMessage({
          type: 'warning',
          text: `Sent ${results.success} invitations, ${results.failed} failed. Check console for details.`
        });
        console.error('Bulk invite errors:', results.errors);
      }

      // Reset form after successful send
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Bulk invite error:', error);
      setMessage({
        type: 'error',
        text: 'Failed to send bulk invitations. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setManualInvites([{
      email: '',
      products: ['dataroom'],
      role: 'viewer',
      customMessage: ''
    }]);
    setCsvFile(null);
    setCsvData([]);
    setCsvErrors([]);
    setMessage(null);
    setProgress({ current: 0, total: 0, status: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Users className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Bulk Invite Users</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('manual')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'manual'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Manual Entry
            </button>
            <button
              onClick={() => setActiveTab('csv')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'csv'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              CSV Import
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          
          {/* Message Display */}
          {message && (
            <div className={`mb-4 p-4 rounded-lg flex items-center ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800'
                : message.type === 'warning'
                ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.type === 'success' 
                ? <CheckCircle className="w-5 h-5 mr-2" />
                : <AlertCircle className="w-5 h-5 mr-2" />
              }
              {message.text}
            </div>
          )}

          {/* Progress Display */}
          {loading && progress.total > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">
                  {progress.status}
                </span>
                <span className="text-sm text-blue-700">
                  {progress.current} / {progress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Manual Entry Tab */}
          {activeTab === 'manual' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Manual Invite Entry ({manualInvites.length} invite{manualInvites.length !== 1 ? 's' : ''})
                </h3>
                <button
                  onClick={addManualInvite}
                  className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Invite
                </button>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {manualInvites.map((invite, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-900">Invite #{index + 1}</h4>
                      {manualInvites.length > 1 && (
                        <button
                          onClick={() => removeManualInvite(index)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={invite.email}
                          onChange={(e) => updateManualInvite(index, 'email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="user@example.com"
                        />
                      </div>

                      {/* Role */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role *
                        </label>
                        <select
                          value={invite.role}
                          onChange={(e) => updateManualInvite(index, 'role', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name} - {role.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Products */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Access * (Select one or more)
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {products.map(product => {
                          const Icon = product.icon;
                          const isSelected = invite.products.includes(product.id);
                          
                          return (
                            <div
                              key={product.id}
                              onClick={() => toggleProduct(index, product.id)}
                              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center">
                                <Icon className={`w-5 h-5 mr-2 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                                <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                  {product.name}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Custom Message */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Message (Optional)
                      </label>
                      <textarea
                        value={invite.customMessage}
                        onChange={(e) => updateManualInvite(index, 'customMessage', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Personal message for this invite..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CSV Import Tab */}
          {activeTab === 'csv' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">CSV Import</h3>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center px-3 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Template
                </button>
              </div>

              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="text-sm text-gray-600 mb-4">
                  <p className="font-medium">Upload CSV file</p>
                  <p>Drag and drop your CSV file here, or click to browse</p>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Choose File
                </label>
                {csvFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {csvFile.name}
                  </p>
                )}
              </div>

              {/* CSV Errors */}
              {csvErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">CSV Validation Errors:</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {csvErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CSV Preview */}
              {csvData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Preview ({csvData.length} valid invite{csvData.length !== 1 ? 's' : ''})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 font-medium text-gray-700">Email</th>
                          <th className="text-left py-2 font-medium text-gray-700">Products</th>
                          <th className="text-left py-2 font-medium text-gray-700">Role</th>
                          <th className="text-left py-2 font-medium text-gray-700">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 5).map((invite, index) => (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-2 text-gray-900">{invite.email}</td>
                            <td className="py-2 text-gray-600">{invite.products.join(', ')}</td>
                            <td className="py-2 text-gray-600">{invite.role}</td>
                            <td className="py-2 text-gray-600 truncate max-w-xs">
                              {invite.customMessage || '-'}
                            </td>
                          </tr>
                        ))}
                        {csvData.length > 5 && (
                          <tr>
                            <td colSpan="4" className="py-2 text-center text-gray-500">
                              ... and {csvData.length - 5} more
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={sendBulkInvites}
              disabled={loading || (activeTab === 'manual' && manualInvites.length === 0) || (activeTab === 'csv' && csvData.length === 0)}
              className={`flex items-center px-6 py-2 text-sm font-medium rounded-lg transition-all ${
                loading || (activeTab === 'manual' && manualInvites.length === 0) || (activeTab === 'csv' && csvData.length === 0)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invites
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkInviteModal;
